import { useCallback } from 'preact/hooks';

// PostHog + GTM analytics hook
// PostHog is loaded via index.html script tag
// GTM dataLayer is pushed to for event forwarding

// Type declaration for import.meta.env
interface ImportMetaEnv {
  DEV: boolean;
}

declare global {
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
  interface Window {
    posthog?: {
      capture: (event: string, properties?: Record<string, unknown>) => void;
    };
    dataLayer?: Record<string, unknown>[];
    _hsq?: Array<[string, ...unknown[]]>;
  }
}

export function useAnalytics() {
  const trackEvent = useCallback((eventName: string, properties?: Record<string, unknown>) => {
    // PostHog
    if (window.posthog) {
      window.posthog.capture(eventName, properties);
    }

    // GTM dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({ event: eventName, ...properties });
    }

    // Dev logging
    if (typeof import.meta.env !== 'undefined' && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[Analytics]', eventName, properties);
    }
  }, []);

  const trackPageView = useCallback((path: string) => {
    // PostHog auto-tracks page views when configured, but we can also capture explicitly
    if (window.posthog) {
      window.posthog.capture('$pageview', { path });
    }

    // GTM virtual page view
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'page_view', page_path: path });
    }

    // HubSpot SPA page view
    const _hsq = (window._hsq = window._hsq || []);
    _hsq.push(['setPath', path]);
    _hsq.push(['trackPageView']);

    // Dev logging
    if (typeof import.meta.env !== 'undefined' && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[Analytics] Page view:', path);
    }
  }, []);

  return { trackEvent, trackPageView };
}
